import React, { useState, useLayoutEffect, useContext, useRef, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import MenuService from '../../service/menu.service';
import UploadService from '../../service/upload.service';
import { ApiContext } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';
import { formatCentsToBRL, parseBRLToCents } from '../../utils/currency';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal/ConfirmDeleteModal';

const toFormState = (item) => {
  const firstSize = (item?.sizes || []).find((s) => s.active !== false) || item?.sizes?.[0];
  return {
    name: item?.name || '',
    description: item?.description || '',
    active: item?.active ?? true,
    images: (item?.images || []).map((img) => ({ id: img.id, url: img.url })),
    hasMultipleSizes: Boolean(item?.hasMultipleSizes ?? (item?.sizes?.length > 1)),
    singlePriceCents: firstSize?.priceCents || 0,
    sizes: (item?.sizes || []).map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description || '',
      priceCents: s.priceCents,
      sortOrder: s.sortOrder ?? 0,
    })),
    variants: (item?.variants || []).map((v) => ({ id: v.id, name: v.name, extraCents: v.extraCents })),
  };
};

const ItemForm = ({ show, setShow, item, categoryId, categoryItems, onSaved }) => {
  const { account } = useContext(ApiContext);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useNotification();
  const [hasMultipleSizes, setHasMultipleSizes] = useState(false);
  const [singlePriceInput, setSinglePriceInput] = useState(formatCentsToBRL(0));
  const [singlePriceCents, setSinglePriceCents] = useState(0);
  const [sizes, setSizes] = useState([]);
  const [variants, setVariants] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const initialStateRef = useRef('');
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [sizeForm, setSizeForm] = useState({
    index: null,
    id: null,
    name: '',
    description: '',
    priceInput: formatCentsToBRL(0),
    priceCents: 0,
    sortOrder: 0,
  });
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [variantForm, setVariantForm] = useState({
    index: null,
    name: '',
    extraInput: formatCentsToBRL(0),
    extraCents: 0,
  });

  useLayoutEffect(() => {
    if (!show) return;
    const initial = toFormState(item);
    setName(initial.name);
    setDescription(initial.description);
    setActive(initial.active);
    setImages(initial.images);
    setHasMultipleSizes(initial.hasMultipleSizes);
    setSinglePriceCents(initial.singlePriceCents);
    setSinglePriceInput(formatCentsToBRL(initial.singlePriceCents));
    setSizes(initial.sizes);
    setVariants(initial.variants);
    setShowSizeModal(false);
    setShowVariantModal(false);
    setConfirmClose(false);
    initialStateRef.current = JSON.stringify(initial);
  }, [item, show]);

  const isDirty = () =>
    JSON.stringify({ name, description, active, images, hasMultipleSizes, singlePriceCents, sizes, variants }) !==
    initialStateRef.current;

  const openAddSize = () => {
    setSizeForm({
      index: null,
      id: null,
      name: '',
      description: '',
      priceInput: formatCentsToBRL(0),
      priceCents: 0,
      sortOrder: sizes.length,
    });
    setShowSizeModal(true);
  };

  const openEditSize = (s, index) => {
    setSizeForm({
      index,
      id: s.id || null,
      name: s.name || '',
      description: s.description || '',
      priceInput: formatCentsToBRL(s.priceCents || 0),
      priceCents: s.priceCents || 0,
      sortOrder: s.sortOrder ?? 0,
    });
    setShowSizeModal(true);
  };

  const closeSizeModal = () => setShowSizeModal(false);

  const handleSizeSave = async (e) => {
    e && e.preventDefault();
    const sizeName = sizeForm.name?.trim();
    const priceCents = Number(sizeForm.priceCents || 0);
    if (!sizeName) return notify('tr', 'danger', 'Nome da medida é obrigatório', 'icon-alert-circle-exc');

    const payload = {
      name: sizeName,
      description: sizeForm.description?.trim() || undefined,
      priceCents,
      sortOrder: Number(sizeForm.sortOrder || 0),
    };

    if (sizeForm.index !== null) {
      if (sizeForm.id) {
        try {
          await MenuService.updateSize(sizeForm.id, payload);
        } catch (err) {
          console.error(err);
          return notify('tr', 'danger', 'Erro ao atualizar medida', 'icon-alert-circle-exc');
        }
      }
      setSizes((prev) => prev.map((s, i) => (i === sizeForm.index ? { ...s, ...payload } : s)));
    } else {
      setSizes((prev) => [...prev, { id: null, ...payload }]);
    }

    setShowSizeModal(false);
  };

  const handleSizeDelete = async (s) => {
    if (sizes.length <= 1) {
      return notify('tr', 'warning', 'Item precisa ter ao menos uma medida', 'icon-alert-circle-exc');
    }
    if (s.id && item && item.id) {
      try {
        await MenuService.deleteSize(s.id);
        setSizes((prev) => prev.filter((x) => x.id !== s.id));
        notify('tr', 'success', 'Medida removida', 'icon-check-2');
        return;
      } catch (err) {
        console.error(err);
        notify('tr', 'danger', 'Erro ao remover medida', 'icon-alert-circle-exc');
        return;
      }
    }
    setSizes((prev) => prev.filter((x) => x !== s));
  };

  const openAddVariant = () => {
    setVariantForm({ index: null, name: '', extraInput: formatCentsToBRL(0), extraCents: 0 });
    setShowVariantModal(true);
  };

  const openEditVariant = (v, index) => {
    setVariantForm({
      index,
      name: v.name || '',
      extraInput: formatCentsToBRL(v.extraCents || 0),
      extraCents: v.extraCents || 0,
    });
    setShowVariantModal(true);
  };

  const closeVariantModal = () => setShowVariantModal(false);

  const handleVariantModalKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleVariantSave = async (e) => {
    e && e.preventDefault();
    const variantName = variantForm.name?.trim();
    const extraCents = Number(variantForm.extraCents || 0);
    if (!variantName) return notify('tr', 'danger', 'Nome do acréscimo é obrigatório', 'icon-alert-circle-exc');

    if (variantForm.index !== null) {
      const current = variants[variantForm.index];
      if (current?.id) {
        try {
          await MenuService.updateVariant(current.id, { name: variantName, extraCents });
        } catch (err) {
          console.error(err);
          return notify('tr', 'danger', 'Erro ao atualizar acréscimo', 'icon-alert-circle-exc');
        }
      }
      setVariants((prev) =>
        prev.map((v, i) => (i === variantForm.index ? { ...v, name: variantName, extraCents } : v))
      );
    } else {
      setVariants((prev) => [...prev, { id: null, name: variantName, extraCents }]);
    }

    setShowVariantModal(false);
  };

  const suggestedVariants = useMemo(() => {
    const used = new Set(variants.map((v) => v.name.trim().toLowerCase()));
    const found = new Map();
    (categoryItems || []).forEach((it) => {
      if (it.id === item?.id) return;
      (it.variants || []).forEach((v) => {
        const key = v.name.trim().toLowerCase();
        if (!used.has(key) && !found.has(key)) found.set(key, { name: v.name.trim(), extraCents: v.extraCents });
      });
    });
    return [...found.values()];
  }, [categoryItems, item, variants]);

  const addSuggestedVariants = (list) => {
    setVariants((prev) => [...prev, ...list.map((v) => ({ id: null, name: v.name, extraCents: v.extraCents }))]);
  };

  const handleVariantDelete = async (v) => {
    if (v.id && item && item.id) {
      try {
        await MenuService.deleteVariant(v.id);
        setVariants((prev) => prev.filter((x) => x.id !== v.id));
        notify('tr', 'success', 'Acréscimo removido', 'icon-check-2');
        return;
      } catch (err) {
        console.error(err);
        notify('tr', 'danger', 'Erro ao remover acréscimo', 'icon-alert-circle-exc');
        return;
      }
    }
    setVariants((prev) => prev.filter((x) => x !== v));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = files.filter((f) => f.type.startsWith('image/')).slice(0, Math.max(0, 3 - images.length));
    if (allowed.length === 0) return alert('Selecione imagens JPEG, PNG ou WebP');

    setIsUploading(true);
    try {
      for (const f of allowed) {
        const res = await UploadService.uploadSingle(f);
        const url = res.url;
        const imgObj = { id: res.id || null, url };
        if (item && item.id) {
          try {
            const created = await MenuService.createMenuItemImage(item.id, { url });
            setImages((prev) => [...prev, { id: created?.id || null, url: created?.url || url }]);
          } catch (err) {
            setImages((prev) => [...prev, imgObj]);
          }
        } else {
          setImages((prev) => [...prev, imgObj]);
        }
      }
    } catch (err) {
      console.error('Upload error', err);
      notify('tr', 'danger', 'Erro ao enviar imagem', 'icon-alert-circle-exc');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (img) => {
    if (img.id && item && item.id) {
      try {
        await MenuService.deleteMenuItemImage(item.id, img.id);
      } catch (err) {
        // ignore
      }
    }
    setImages((prev) => prev.filter((i) => i !== img));
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (hasMultipleSizes && !sizes.length) {
      return notify('tr', 'danger', 'Adicione ao menos uma medida', 'icon-alert-circle-exc');
    }
    if (!hasMultipleSizes && singlePriceCents <= 0) {
      return notify('tr', 'danger', 'Informe o preço do item', 'icon-alert-circle-exc');
    }
    if (!hasMultipleSizes && sizes.length > 1) {
      return notify('tr', 'danger', 'Remova as medidas extras antes de salvar como tamanho único', 'icon-alert-circle-exc');
    }

    const resolvedAccountId = (account && account.id) || item?.accountId;
    const isEditing = !!(item && item.id);

    const payload = {
      name,
      description,
      categoryId: categoryId || item?.categoryId,
      accountId: resolvedAccountId,
      active,
      hasMultipleSizes,
      ...(hasMultipleSizes
        ? isEditing
          ? {}
          : {
              sizes: sizes.map((s, idx) => ({
                name: s.name,
                description: s.description || undefined,
                priceCents: s.priceCents,
                sortOrder: s.sortOrder ?? idx,
              })),
            }
        : { priceCents: singlePriceCents }),
    };

    let savedItem = null;
    setIsSaving(true);
    if (item && item.id) {
      try {
        savedItem = await MenuService.updateMenuItem(item.id, payload);
        notify('tr', 'success', 'Item atualizado com sucesso', 'icon-check-2');
      } catch (err) {
        console.error(err);
        notify('tr', 'danger', 'Erro ao atualizar item', 'icon-alert-circle-exc');
        setIsSaving(false);
        return;
      }
    } else {
      try {
        savedItem = await MenuService.createMenuItem(payload);
        notify('tr', 'success', 'Item criado com sucesso', 'icon-check-2');
      } catch (err) {
        console.error(err);
        notify('tr', 'danger', 'Erro ao criar item', 'icon-alert-circle-exc');
        setIsSaving(false);
        return;
      }
    }

    try {
      const itemId = savedItem?.id || item?.id;
      if (itemId) {
        const newImages = [];
        for (const img of images) {
          if (!img.id) {
            try {
              const created = await MenuService.createMenuItemImage(itemId, { url: img.url });
              newImages.push({ id: created.id, url: created.url });
            } catch (err) {
              newImages.push(img);
            }
          } else {
            newImages.push(img);
          }
        }
        setImages(newImages);

        const newSizes = [];
        if (isEditing && hasMultipleSizes) {
          for (const s of sizes) {
            if (!s.id) {
              try {
                const created = await MenuService.addSize(itemId, {
                  name: s.name,
                  description: s.description || undefined,
                  priceCents: s.priceCents,
                  sortOrder: s.sortOrder ?? 0,
                });
                newSizes.push({
                  id: created.id,
                  name: created.name,
                  description: created.description || '',
                  priceCents: created.priceCents,
                  sortOrder: created.sortOrder ?? 0,
                });
              } catch (err) {
                newSizes.push(s);
              }
            } else {
              newSizes.push(s);
            }
          }
          setSizes(newSizes);
        } else if (savedItem?.sizes?.length) {
          setSizes(
            savedItem.sizes.map((s) => ({
              id: s.id,
              name: s.name,
              description: s.description || '',
              priceCents: s.priceCents,
              sortOrder: s.sortOrder ?? 0,
            }))
          );
        }

        const newVariants = [];
        for (const v of variants) {
          if (!v.id) {
            try {
              const created = await MenuService.addVariant(itemId, { name: v.name, extraCents: v.extraCents });
              newVariants.push({ id: created.id, name: created.name, extraCents: created.extraCents });
            } catch (err) {
              newVariants.push(v);
            }
          } else {
            newVariants.push(v);
          }
        }
        setVariants(newVariants);
      }
    } catch (err) {
      // ignore
    } finally {
      setIsSaving(false);
    }

    if (setShow) setShow(false);
    if (onSaved) onSaved();
  };

  const handleClose = () => {
    if (setShow) setShow(false);
  };

  const requestClose = () => {
    if (isSaving) return;
    if (isDirty()) return setConfirmClose(true);
    handleClose();
  };

  const fileInputRef = useRef(null);

  const openFileChooser = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  return (
    <>
    <Modal isOpen={!!show} toggle={requestClose} size="lg" modalClassName="modal-form">
      <ModalHeader toggle={requestClose}>{item ? 'Editar Item' : 'Novo Item'}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
          <FormGroup>
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </FormGroup>

          <Modal isOpen={showSizeModal} toggle={closeSizeModal}>
            <ModalHeader toggle={closeSizeModal}>{sizeForm.index !== null ? 'Editar medida' : 'Nova medida'}</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Medida (ex: 300ml, 500g, 1 unidade)</Label>
                <Input
                  value={sizeForm.name}
                  onChange={(e) => setSizeForm((s) => ({ ...s, name: e.target.value }))}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Preço</Label>
                <Input
                  value={sizeForm.priceInput}
                  onChange={(e) => {
                    const cents = parseBRLToCents(e.target.value);
                    setSizeForm((s) => ({
                      ...s,
                      priceInput: formatCentsToBRL(cents),
                      priceCents: cents,
                    }));
                  }}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Descrição (opcional)</Label>
                <Input
                  type="textarea"
                  value={sizeForm.description}
                  onChange={(e) => setSizeForm((s) => ({ ...s, description: e.target.value }))}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter className="pt-3">
              <div className="w-100 d-flex justify-content-end">
                <Button color="secondary" onClick={closeSizeModal} className="mr-2" type="button">
                  Cancelar
                </Button>
                <Button color="primary" onClick={handleSizeSave} type="button">
                  Salvar
                </Button>
              </div>
            </ModalFooter>
          </Modal>

          <Modal isOpen={showVariantModal} toggle={closeVariantModal} onKeyDown={handleVariantModalKeyDown}>
            <ModalHeader toggle={closeVariantModal}>{variantForm.index !== null ? 'Editar acréscimo' : 'Novo acréscimo'}</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Nome</Label>
                <Input
                  value={variantForm.name}
                  onChange={(e) => setVariantForm((s) => ({ ...s, name: e.target.value }))}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Valor adicional</Label>
                <Input
                  value={variantForm.extraInput}
                  onChange={(e) => {
                    const cents = parseBRLToCents(e.target.value);
                    setVariantForm((s) => ({
                      ...s,
                      extraInput: formatCentsToBRL(cents),
                      extraCents: cents,
                    }));
                  }}
                  required
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter className="pt-3">
              <div className="w-100 d-flex justify-content-end">
                <Button color="secondary" onClick={closeVariantModal} className="mr-2" type="button">
                  Cancelar
                </Button>
                <Button color="primary" onClick={handleVariantSave} type="button">
                  Salvar
                </Button>
              </div>
            </ModalFooter>
          </Modal>

          <FormGroup>
            <Label>Descrição geral</Label>
            <Input type="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span className="form-check-sign">
                <span className="check" />
              </span>{' '}
              Ativo
            </Label>
          </FormGroup>
          <FormGroup className="mt-3">
            <Label>Imagens (máx 3)</Label>
            <div className="d-flex align-items-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={openFileChooser}
                title="Adicionar imagens"
                aria-label="Adicionar imagens"
                style={{
                  width: 36,
                  height: 32,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                <i className="tim-icons icon-attach-87" />
              </button>
              {isUploading && <span className="ml-2">Enviando...</span>}
            </div>
            <div className="d-flex mt-2" style={{ gap: 8 }}>
              {images.map((img, idx) => (
                <div key={img.id || img.url || idx} style={{ position: 'relative' }}>
                  <img
                    src={img.url}
                    alt={`img-${idx}`}
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(img)}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      background: '#fff',
                      borderRadius: '50%',
                      border: '1px solid #ccc',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </FormGroup>

          <FormGroup>
            <Label>Este item tem múltiplos tamanhos?</Label>
            <div className="mt-2">
              <div
                className="d-flex align-items-center p-2 mb-2"
                onClick={() => setHasMultipleSizes(false)}
                style={{
                  cursor: 'pointer',
                  borderRadius: 4,
                  background: !hasMultipleSizes ? 'rgba(29, 140, 248, 0.12)' : 'transparent',
                }}
              >
                <Input
                  type="radio"
                  name="hasMultipleSizes"
                  checked={!hasMultipleSizes}
                  onChange={() => setHasMultipleSizes(false)}
                  style={{ marginRight: 8 }}
                />
                <span>Não — tamanho único</span>
              </div>
              <div
                className="d-flex align-items-center p-2"
                onClick={() => setHasMultipleSizes(true)}
                style={{
                  cursor: 'pointer',
                  borderRadius: 4,
                  background: hasMultipleSizes ? 'rgba(29, 140, 248, 0.12)' : 'transparent',
                }}
              >
                <Input
                  type="radio"
                  name="hasMultipleSizes"
                  checked={hasMultipleSizes}
                  onChange={() => setHasMultipleSizes(true)}
                  style={{ marginRight: 8 }}
                />
                <span>Sim — vários tamanhos</span>
              </div>
            </div>
          </FormGroup>

          {!hasMultipleSizes ? (
            <FormGroup>
              <Label>Preço *</Label>
              <Input
                value={singlePriceInput}
                onChange={(e) => {
                  const cents = parseBRLToCents(e.target.value);
                  setSinglePriceCents(cents);
                  setSinglePriceInput(formatCentsToBRL(cents));
                }}
                required
              />
            </FormGroup>
          ) : (
          <FormGroup>
            <Label>Medidas / Tamanhos *</Label>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <div style={{ flex: 1 }}>
                {sizes.length === 0 && <div className="text-muted">Nenhuma medida — obrigatório</div>}
                {sizes.map((s, index) => (
                  <div key={s.id || `new-${index}`} className="d-flex align-items-center" style={{ gap: 8, marginTop: 6 }}>
                    <div style={{ flex: 1 }}>
                      <strong>{s.name}</strong>{' '}
                      <span className="text-muted">({formatCentsToBRL(s.priceCents)})</span>
                      {s.description && (
                        <div className="text-muted small">{s.description}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => openEditSize(s, index)}
                        title="Editar"
                      >
                        <i className="tim-icons icon-pencil" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleSizeDelete(s)}
                        title="Remover"
                      >
                        <i className="tim-icons icon-trash-simple" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={openAddSize}
              title="Adicionar medida"
            >
              <i className="tim-icons icon-simple-add" /> Adicionar medida
            </button>
          </FormGroup>
          )}

          <FormGroup className="mt-3">
            <Label>Acréscimos (opcional)</Label>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <div style={{ flex: 1 }}>
                {variants.length === 0 && <div className="text-muted">Nenhum acréscimo</div>}
                {variants.map((v, index) => (
                  <div key={v.id || `new-${index}`} className="d-flex align-items-center" style={{ gap: 8, marginTop: 6 }}>
                    <div style={{ flex: 1 }}>
                      {v.name} <small className="text-muted">(+{formatCentsToBRL(v.extraCents)})</small>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => openEditVariant(v, index)}
                        title="Editar"
                      >
                        <i className="tim-icons icon-pencil" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleVariantDelete(v)}
                        title="Remover"
                      >
                        <i className="tim-icons icon-trash-simple" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary mt-2"
              onClick={openAddVariant}
              title="Adicionar acréscimo"
            >
              <i className="tim-icons icon-simple-add" /> Adicionar acréscimo
            </button>
            {suggestedVariants.length > 0 && (
              <div className="variant-suggestions mt-3">
                <div className="variant-suggestions__header">
                  <span>Já usados nesta categoria</span>
                  {suggestedVariants.length > 1 && (
                    <button
                      type="button"
                      className="variant-suggestions__all"
                      onClick={() => addSuggestedVariants(suggestedVariants)}
                    >
                      Adicionar todos
                    </button>
                  )}
                </div>
                <div className="variant-suggestions__list">
                  {suggestedVariants.map((v) => (
                    <button
                      key={v.name}
                      type="button"
                      className="variant-chip"
                      onClick={() => addSuggestedVariants([v])}
                      title={`Adicionar ${v.name}`}
                    >
                      <i className="tim-icons icon-simple-add" />
                      {v.name}
                      <span className="variant-chip__price">
                        {v.extraCents > 0 ? `+${formatCentsToBRL(v.extraCents)}` : 'Grátis'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter className="pt-3">
          <div className="w-100 d-flex justify-content-end">
            <Button color="secondary" onClick={handleClose} className="mr-2">
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
    <ConfirmDeleteModal
      isOpen={confirmClose}
      toggle={() => setConfirmClose(false)}
      title="Descartar alterações?"
      message="Você tem alterações não salvas neste item. Se fechar agora, elas serão perdidas."
      btnConfirm="Descartar"
      btnCancel="Continuar editando"
      onConfirm={handleClose}
    />
    </>
  );
};

export default ItemForm;
